import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AB_TEST_USER_JOURNEY_TRACKER, UserJourneyTrackerServiceInterface } from '@campus/ab-test';
import { EduContent, EduContentBookInterface, LearnosityItemsConfigInterface } from '@campus/dal';
import { ENVIRONMENT_UI_TOKEN, EnvironmentUIInterface } from '@campus/environment';
import { ConfirmationModalComponent, ConfirmationModalDataInterface } from '@campus/ui';
import { filter, Subject } from 'rxjs';
import { LearnosityDialogComponentType, LearnosityErrorCodeInterface } from '../../interfaces';
import { LEARNOSITY_WINDOW } from '../../services';
import { LearnosityOverlayService } from '../../services/learnosity-overlay.service';
import { GradingComponent } from '../grading/grading.component';

import '@diekeure/dcr-components/dcr-dialog.js';
import '@diekeure/dcr-components/dcr-filled-button.js';
import '@diekeure/dcr-components/dcr-inline-button.js';

@Component({
  standalone: false,
  selector: 'campus-learnosity-dialog',
  templateUrl: './learnosity-dialog.component.html',
  styleUrls: ['./learnosity-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: '[ block relative ] [ h-full w-full m-0 overflow-auto ] [ ] [ background ] ca' },
})
export class LearnosityDialogComponent implements OnInit, OnDestroy {
  @Input() config: LearnosityItemsConfigInterface;
  @Input() component: LearnosityDialogComponentType;
  @Input() eduContent: EduContent;

  public eduContentBookId: number;
  public learningDomainId: number | null;
  public eduContentTOCId: number | null;
  public isComponentsLoaded = false;

  public supportMailAddress: string;
  public playerError: WritableSignal<string> = signal(null);
  public learnosityPlayerError: WritableSignal<LearnosityErrorCodeInterface> = signal(null);

  @ViewChild(GradingComponent, { static: false }) gradingComponent: GradingComponent;

  private matDialog = inject(MatDialog);
  private overlayService = inject(LearnosityOverlayService);
  private window: Window = inject(LEARNOSITY_WINDOW);
  private userJourneyTracker: UserJourneyTrackerServiceInterface = inject(AB_TEST_USER_JOURNEY_TRACKER);
  private uiEnvironment: EnvironmentUIInterface = inject(ENVIRONMENT_UI_TOKEN);

  private destroy$ = new Subject<void>();

  get isLearnosityComponent(): boolean {
    return this.component === 'assessment' || this.component === 'grading';
  }

  get shouldPreventClose(): boolean {
    return !this.isComponentsLoaded && this.isLearnosityComponent;
  }

  ngOnInit(): void {
    this.eduContentBookId = this.config?.eduContentBookId || this.getEduContentBookId();
    this.learningDomainId = this.config?.learningDomainId || null;
    this.eduContentTOCId = this.config?.eduContentTOCId || null;
    this.supportMailAddress = this.uiEnvironment?.contact?.general?.mailAddress;

    if (!this.isLearnosityComponent) {
      this.isComponentsLoaded = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async close() {
    if (this.shouldPreventClose) {
      return;
    }
    this.overlayService.close();
  }

  public onCloseClicked(closedViaButton: boolean = true) {
    if (this.shouldPreventClose) return;

    const learnosityActivityId = this.window.itemsApp?.getActivity()?.activity_id;
    const learnositySessionId = this.window.itemsApp?.getActivity()?.session_id;

    if (this.hasUnsavedGradingChanges()) {
      this.openSaveConfirmation(closedViaButton, learnosityActivityId, learnositySessionId);
      return;
    }

    this.trackCloseIfNeeded(closedViaButton, learnosityActivityId, learnositySessionId);
    this.overlayService.close();
  }

  public onComponentsLoaded() {
    this.isComponentsLoaded = true;
  }

  public onPlayerLoadError(event: LearnosityErrorCodeInterface) {
    this.learnosityPlayerError.set(event);
    this.playerError.set(
      `Er is een onverwachte fout opgetreden bij het opstarten van de oefening. Probeer het later opnieuw! Indien de fout zich blijft herhalen, neem dan contact op met de helpdesk. Foutcode: ${event.code}`
    );
  }

  public onPlayerErrorDialogConfirm() {
    this.overlayService.close();
  }

  public onPlayerErrorDialogMail() {
    const error = this.learnosityPlayerError();
    const mailTo = `mailto:${this.supportMailAddress}`;
    const subject = 'Fout bij het opstarten van een oefening';
    const body = `${encodeURIComponent(`
      De volgende fout kwam voor bij het opstarten van een oefening:

      Code: ${error.code}
      Url: ${this.window.location.href}
      Message: ${error.msg}
    `)}`;

    this.window.location.href = `${mailTo}?subject=${subject}&body=${body}`;
  }

  private hasUnsavedGradingChanges(): boolean {
    return (
      this.config?.request?.rendering_type === 'inline' &&
      this.config?.request?.state === 'initial' &&
      this.eduContent?.questions?.some((question) => question.needsGrading)
    );
  }

  private openSaveConfirmation(
    closedViaButton: boolean,
    learnosityActivityId: string,
    learnositySessionId: string
  ): void {
    const dialogData: ConfirmationModalDataInterface = {
      title: 'Wil je opslaan?',
      message: 'Indien niet verlies je al je aanpassingen.',
    };

    const dialogRef = this.matDialog.open(ConfirmationModalComponent, { data: dialogData });

    dialogRef
      .afterClosed()
      .pipe(filter((confirmed) => confirmed !== undefined))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.saveAndClose(closedViaButton, learnosityActivityId, learnositySessionId);
        } else {
          this.overlayService.close();
        }
      });
  }

  private async saveAndClose(
    closedViaButton: boolean,
    learnosityActivityId: string,
    learnositySessionId: string
  ): Promise<void> {
    await this.gradingComponent.save();
    this.trackCloseIfNeeded(closedViaButton, learnosityActivityId, learnositySessionId);
    this.overlayService.close();
  }

  private trackCloseIfNeeded(
    closedViaButton: boolean,
    learnosityActivityId: string,
    learnositySessionId: string
  ): void {
    if (!closedViaButton) return;

    const event = this.hasUnsavedGradingChanges()
      ? 'learnosity_dialog_close_button_clicked_after_grading_confirmation'
      : 'learnosity_dialog_close_button_clicked';

    this.userJourneyTracker.trackUserInteraction(event, 'campus-learnosity-dialog', {
      learnosityActivityId,
      learnositySessionId,
    });
  }

  private getEduContentBookId(): number {
    const eduContentMetadata = this.eduContent?.metadata;
    const eduContentBookIds =
      eduContentMetadata?.eduContentBooks?.map((book: EduContentBookInterface) => book.id) || [];
    return eduContentBookIds?.[0];
  }
}
